import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useLayoutEffect,
  useCallback,
} from "react";
import { ChevronDown } from "lucide-react";

// ─────────────────────────────────────────────────────────────────────────────
// Shared cup-bracket rendering.
//
// Extracted from the former standalone CupWidget so it can be embedded inside
// the StandingsWidget body (in place of the league table) when a CUP is picked
// from the dropdown. Real team crests are kept (TeamMark), and every cup state
// the design covers is preserved: played, scheduled, tbd ("wird ausgelost"),
// two-leg, penalties (i.E.), and the empty "Auslosung folgt" state.
//
// The bracket is laid out to FIT a compact widget area: the outer scroller
// (`.cup-scroll`) scrolls BOTH horizontally (wide brackets) and vertically
// (mouse wheel) within whatever height the parent gives it.
// ─────────────────────────────────────────────────────────────────────────────

const cupTokens = {
  primary: "#2183FE",
  win: "#10B981",
  sched: "#2183FE",
  tbd: "#9CA3AF",
};

// Defensive render cap (defense-in-depth alongside the backend tie cap).
// A pathological payload (e.g. a group-stage "cup" with thousands of ties)
// would otherwise produce a multi-100k-px body and thousands of DOM nodes,
// freezing the widget. We never render more than this many ties per round; a
// real knockout cup tops out at 64 ties (round of 64), so normal brackets are
// completely unaffected. Anything beyond the cap is summarised with a small
// "+X weitere" note instead of being mounted.
const MAX_TIES_PER_ROUND = 64;

function deriveShort(name) {
  const letters = (name || "").replace(/[^0-9A-Za-zÀ-ž]/g, "");
  return letters.slice(0, 3).toUpperCase() || "T";
}

function tieWinnerTeam(tie) {
  if (!tie || !tie.score || !tie.home || !tie.away) return null;
  return tie.score.winner === "home" ? tie.home : tie.away;
}

// Team logo — REAL crest, with a graceful monogram fallback to the short code
// when a logo URL is missing or fails to load.
function TeamMark({ team, size = 22 }) {
  const [errored, setErrored] = useState(false);
  useEffect(() => {
    setErrored(false);
  }, [team?.logo]);

  if (!team) return null;

  const hasLogo = Boolean(team.logo) && !errored;
  if (hasLogo) {
    return (
      <img
        src={team.logo}
        alt=""
        width={size}
        height={size}
        loading="lazy"
        onError={() => setErrored(true)}
        className="cup-team__logo"
        style={{ width: size, height: size }}
      />
    );
  }

  const r = size * 0.22;
  const short = (team.short || deriveShort(team.name)).slice(0, 2);
  return (
    <span
      className="cup-team__mono"
      style={{
        width: size,
        height: size,
        borderRadius: r,
        fontSize: size * 0.4,
      }}
      aria-hidden="true"
    >
      {short}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Match card pieces
// ─────────────────────────────────────────────────────────────────────────────
function TeamRow({ team, win, dim, sub, scoreNode }) {
  if (!team) {
    return (
      <div className="cup-team cup-team--empty">
        <span className="cup-team__ph" />
        <span className="cup-team__name cup-team__name--ph">—</span>
      </div>
    );
  }
  return (
    <div
      className={
        "cup-team" + (win ? " cup-team--win" : "") + (dim ? " cup-team--dim" : "")
      }
    >
      <TeamMark team={team} size={22} />
      <span className="cup-team__name">
        {team.name}
        {sub && <span className="cup-team__sub">{sub}</span>}
      </span>
      <span className="cup-team__score">{scoreNode}</span>
    </div>
  );
}

function ScoreBox({ value, win }) {
  return (
    <span className={"cup-scorebox" + (win ? " cup-scorebox--win" : "")}>
      {value}
    </span>
  );
}

function MatchCard({ tie, isFinal, registerRef }) {
  // TBD — "wird ausgelost"
  if (tie.status === "tbd") {
    return (
      <div
        className={"cup-card cup-card--tbd" + (isFinal ? " cup-card--final" : "")}
        ref={registerRef}
      >
        <div className="cup-card__tbd">
          <span className="cup-card__tbd-ico">🏆</span>
          <span className="cup-card__tbd-txt">To be drawn</span>
          <span className="cup-card__tbd-sub">{isFinal ? "Final" : "Still open"}</span>
        </div>
      </div>
    );
  }

  // Two-legged
  if (tie.status === "twoleg") {
    const legs = tie.legs || [null, null];
    const bothPlayed = legs[0] && legs[1];
    const shootout = tie.shootout || null;
    let aggH = null;
    let aggA = null;
    let win = null;
    if (bothPlayed) {
      aggH = legs[0].hs + legs[1].hs;
      aggA = legs[0].as + legs[1].as;
      // Prefer the backend-decided winner (covers penalty shootouts when the
      // aggregate is level); fall back to the aggregate comparison.
      if (tie.winner === "home" || tie.winner === "away") {
        win = tie.winner;
      } else if (aggH === aggA) {
        win = null;
      } else {
        win = aggH > aggA ? "home" : "away";
      }
    }
    const legCell = (side) => (
      <span className="cup-team__legs">
        <span className="cup-leg">{legs[0] ? legs[0][side] : "–"}</span>
        <span className={"cup-leg" + (legs[1] ? "" : " cup-leg--ghost")}>
          {legs[1] ? legs[1][side] : "–"}
        </span>
      </span>
    );
    const meta = tie.legMeta || [{}, {}];
    return (
      <div className="cup-card" data-status="twoleg" ref={registerRef}>
        <div className="cup-card__rows">
          <TeamRow
            team={tie.home}
            win={win === "home"}
            dim={win === "away"}
            scoreNode={legCell("hs")}
          />
          <TeamRow
            team={tie.away}
            win={win === "away"}
            dim={win === "home"}
            scoreNode={legCell("as")}
          />
        </div>
        <div className="cup-card__foot">
          <span className={"cup-tag cup-tag--" + (meta[0]?.status === "played" ? "played" : "sched")}>
            1st leg {meta[0]?.date || ""}
          </span>
          <span className={"cup-tag cup-tag--" + (meta[1]?.status === "played" ? "played" : "sched")}>
            2nd leg {meta[1]?.date || ""}
          </span>
          {bothPlayed && (
            <span className="cup-tag cup-tag--agg">
              Aggregate {aggH}-{aggA}
            </span>
          )}
          {shootout && (
            <span className="cup-tag cup-tag--pens">
              i.E. {shootout.hp}–{shootout.ap}
            </span>
          )}
        </div>
      </div>
    );
  }

  // Scheduled single leg
  if (tie.status === "scheduled") {
    return (
      <div className="cup-card" data-status="scheduled" ref={registerRef}>
        <div className="cup-card__rows">
          <TeamRow team={tie.home} scoreNode={<span className="cup-vs">–</span>} />
          <TeamRow team={tie.away} scoreNode={<span className="cup-vs">–</span>} />
        </div>
        <div className="cup-card__foot">
          <span className="cup-tag cup-tag--sched">Scheduled</span>
          {tie.date && <span className="cup-tag cup-tag--date">{tie.date}</span>}
        </div>
      </div>
    );
  }

  // Played single leg
  const s = tie.score || {};
  const pens = s.hp != null;
  return (
    <div className="cup-card" data-status="played" ref={registerRef}>
      <div className="cup-card__rows">
        <TeamRow
          team={tie.home}
          win={s.winner === "home"}
          dim={s.winner === "away"}
          scoreNode={<ScoreBox value={s.hs} win={s.winner === "home"} />}
        />
        <TeamRow
          team={tie.away}
          win={s.winner === "away"}
          dim={s.winner === "home"}
          sub={tie.awayFrom}
          scoreNode={<ScoreBox value={s.as} win={s.winner === "away"} />}
        />
      </div>
      <div className="cup-card__foot">
        {tie.date && <span className="cup-tag cup-tag--date">{tie.date}</span>}
        {s.aet && <span className="cup-tag cup-tag--aet">n.V.</span>}
        {pens && (
          <span className="cup-tag cup-tag--pens">
            i.E. {s.hp}–{s.ap}
          </span>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Bracket with computed SVG connectors. Reusable: pass a `cup` payload (rounds)
// and an optional `loading` flag. Designed to live inside a height-constrained
// parent — it scrolls in both axes.
// ─────────────────────────────────────────────────────────────────────────────
function CupBracket({ cup, loading = false }) {
  const scrollRef = useRef(null);
  const contentRef = useRef(null);
  const cardRefs = useRef({});
  const [paths, setPaths] = useState([]);
  const [dims, setDims] = useState({ w: 0, h: 0 });
  const [canL, setCanL] = useState(false);
  const [canR, setCanR] = useState(false);

  const slotH = 86;
  const maxCount = useMemo(
    () =>
      cup && cup.rounds && cup.rounds.length
        ? Math.max(
            ...cup.rounds.map((r) =>
              // Clamp to the render cap so a pathological round can never blow
              // up the body height (and therefore the laid-out DOM).
              Math.min((r.ties || []).length, MAX_TIES_PER_ROUND),
            ),
          )
        : 1,
    [cup],
  );
  const bodyH = maxCount * slotH;

  const registerRef = useCallback(
    (id) => (el) => {
      if (el) cardRefs.current[id] = el;
      else delete cardRefs.current[id];
    },
    [],
  );

  const recompute = useCallback(() => {
    if (!cup || !cup.rounds || !contentRef.current) return;
    const out = [];
    for (let r = 0; r < cup.rounds.length - 1; r++) {
      const a = cup.rounds[r];
      const b = cup.rounds[r + 1];
      const aTies = a.ties || [];
      const bTies = b.ties || [];
      const feeder = aTies.length === bTies.length;
      // Only the rendered (capped) ties have card refs; iterating beyond the cap
      // would be wasted work on a pathological round and never finds a ref.
      aTies.slice(0, MAX_TIES_PER_ROUND).forEach((child, i) => {
        const parent = feeder ? bTies[i] : bTies[Math.floor(i / 2)];
        if (!parent) return;
        const ce = cardRefs.current[child.id];
        const pe = cardRefs.current[parent.id];
        if (!ce || !pe) return;
        const x1 = ce.offsetLeft + ce.offsetWidth;
        const y1 = ce.offsetTop + ce.offsetHeight / 2;
        const x2 = pe.offsetLeft;
        const y2 = pe.offsetTop + pe.offsetHeight / 2;
        const midX = x1 + (x2 - x1) / 2;
        const win = child.status === "played" ? !!tieWinnerTeam(child) : false;
        out.push({
          d: `M${x1} ${y1} H${midX} V${y2} H${x2}`,
          key: `${child.id}->${parent.id}`,
          win,
        });
      });
    }
    const el = contentRef.current;
    setDims({ w: el.scrollWidth, h: el.offsetHeight });
    setPaths(out);
  }, [cup]);

  useLayoutEffect(() => {
    recompute();
    const ro = new ResizeObserver(recompute);
    if (contentRef.current) ro.observe(contentRef.current);
    return () => ro.disconnect();
  }, [recompute, slotH, bodyH]);

  const updateScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanL(el.scrollLeft > 4);
    setCanR(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);
  useEffect(() => {
    updateScroll();
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateScroll, { passive: true });
    const ro = new ResizeObserver(updateScroll);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", updateScroll);
      ro.disconnect();
    };
  }, [updateScroll, cup, slotH]);
  const scrollBy = (dir) => {
    const el = scrollRef.current;
    if (el) el.scrollBy({ left: dir * Math.round(el.clientWidth * 0.7), behavior: "smooth" });
  };

  // Loading state (initial fetch for a cup, before any rounds arrive).
  if (loading && (!cup || !cup.rounds || cup.rounds.length === 0)) {
    return (
      <div className="cup-empty">
        <div className="cup-empty__ico">🏆</div>
        <div className="cup-empty__title">Loading…</div>
      </div>
    );
  }

  // Empty state — "Auslosung folgt".
  if (!cup || !cup.rounds || cup.rounds.length === 0) {
    return (
      <div className="cup-empty">
        <div className="cup-empty__ico">🏆</div>
        <div className="cup-empty__title">Draw coming soon</div>
        <div className="cup-empty__sub">
          This cup's bracket syncs automatically once the data is available.
        </div>
      </div>
    );
  }

  return (
    <div className="cup-bracketwrap">
      <button
        className={"cup-scrollnav cup-scrollnav--l" + (canL ? "" : " cup-scrollnav--off")}
        onClick={() => scrollBy(-1)}
        aria-label="Back"
        tabIndex={canL ? 0 : -1}
      >
        <ChevronDown size={16} style={{ transform: "rotate(90deg)" }} />
      </button>
      <button
        className={"cup-scrollnav cup-scrollnav--r" + (canR ? "" : " cup-scrollnav--off")}
        onClick={() => scrollBy(1)}
        aria-label="Next"
        tabIndex={canR ? 0 : -1}
      >
        <ChevronDown size={16} style={{ transform: "rotate(-90deg)" }} />
      </button>
      <div className={"cup-fade cup-fade--l" + (canL ? "" : " cup-fade--off")} aria-hidden="true" />
      <div className={"cup-fade cup-fade--r" + (canR ? "" : " cup-fade--off")} aria-hidden="true" />

      <div className="cup-scroll" ref={scrollRef}>
        <div className="cup-bracket" ref={contentRef}>
          <svg
            className="cup-conns"
            width={dims.w}
            height={dims.h}
            viewBox={`0 0 ${dims.w} ${dims.h}`}
            aria-hidden="true"
          >
            {paths.map((p) => (
              <path
                key={p.key}
                d={p.d}
                fill="none"
                className={"cup-conn" + (p.win ? " cup-conn--win" : "")}
              />
            ))}
          </svg>
          {cup.rounds.map((round) => {
            const allTies = round.ties || [];
            // Defensive cap: never mount more than MAX_TIES_PER_ROUND cards.
            const shownTies = allTies.slice(0, MAX_TIES_PER_ROUND);
            const overflow = allTies.length - shownTies.length;
            return (
              <div className="cup-round" key={round.key}>
                <div className="cup-round__head">
                  <span className="cup-round__label">{round.label}</span>
                  <span className="cup-round__date">{round.date}</span>
                </div>
                <div className="cup-round__body" style={{ height: bodyH }}>
                  {shownTies.map((tie) => (
                    <div className="cup-tie-wrap" key={tie.id}>
                      <MatchCard
                        tie={tie}
                        isFinal={round.key === "f"}
                        registerRef={registerRef(tie.id)}
                      />
                    </div>
                  ))}
                  {overflow > 0 && (
                    <div className="cup-round__more" aria-label={`${overflow} weitere Partien werden nicht angezeigt`}>
                      +{overflow} weitere
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Legend bar for the cup states — meant to sit where the table footer / matchday
// legend would otherwise be, so the embedding widget can keep a consistent look.
// ─────────────────────────────────────────────────────────────────────────────
function CupLegend() {
  return (
    <div className="cup-legendbar">
      <span className="cup-leg-item">
        <i style={{ background: cupTokens.win }} />
        Played
      </span>
      <span className="cup-leg-item">
        <i style={{ background: cupTokens.sched }} />
        Scheduled
      </span>
      <span className="cup-leg-item">
        <i style={{ background: cupTokens.tbd }} />
        Open
      </span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Scoped CSS — ported faithfully from the design. The ONLY layout change vs the
// standalone widget: `.cup-bracketwrap`/`.cup-scroll` now fill the parent height
// and scroll on BOTH axes so the bracket fits the compact standings widget body.
// ─────────────────────────────────────────────────────────────────────────────
export const CUP_BRACKET_CSS = `
.cup-bracketscope {
  --cup-primary: #2183FE;
  --cup-bg: #FFFFFF;
  --cup-bg-subtle: #F5F7FA;
  --cup-hover: #EBF3FF;
  --cup-text: #1A1A2E;
  --cup-text-2: #6B7280;
  --cup-text-3: #9CA3AF;
  --cup-border: #E5E7EB;
  --cup-line: #D6DEE8;
  --cup-win: #10B981;
  color: var(--cup-text);
  height: 100%;
  display: flex;
  flex-direction: column;
  min-height: 0;
}
.cup-bracketscope * { box-sizing: border-box; }

.cup-bracketscope .cup-bracketwrap {
  position: relative;
  flex: 1;
  min-height: 0;
  display: flex;
}
/* Scroll BOTH axes so wide + tall brackets fit a compact widget body.
   Vertical = mouse wheel; horizontal = trackpad/shift-wheel/nav buttons. */
.cup-bracketscope .cup-scroll {
  flex: 1;
  min-width: 0;
  overflow: auto;
  scrollbar-width: thin;
  scrollbar-color: #C5D2E2 transparent;
}
.cup-bracketscope .cup-scroll::-webkit-scrollbar { height: 9px; width: 9px; }
.cup-bracketscope .cup-scroll::-webkit-scrollbar-thumb { background: #C9D4E1; border-radius: 99px; border: 2px solid var(--cup-bg); }
.cup-bracketscope .cup-bracket { position: relative; display: flex; gap: 40px; padding: 18px 22px 22px; width: max-content; }
.cup-bracketscope .cup-conns { position: absolute; top: 0; left: 0; z-index: 0; overflow: visible; pointer-events: none; }
.cup-bracketscope .cup-conn { stroke: var(--cup-line); stroke-width: 1.6; }
.cup-bracketscope .cup-conn--win { stroke: var(--cup-primary); opacity: 0.55; stroke-width: 1.8; }
.cup-bracketscope .cup-round { z-index: 1; display: flex; flex-direction: column; width: 210px; flex: 0 0 210px; }
.cup-bracketscope .cup-round__head { display: flex; flex-direction: column; gap: 2px; padding: 0 2px 12px; }
.cup-bracketscope .cup-round__label { font-size: 11px; font-weight: 800; letter-spacing: 0.05em; text-transform: uppercase; color: var(--cup-text); }
.cup-bracketscope .cup-round__date { font-size: 11px; color: var(--cup-text-2); font-weight: 500; }
.cup-bracketscope .cup-round__body { display: flex; flex-direction: column; justify-content: space-around; }
.cup-bracketscope .cup-round__more { margin-top: 8px; align-self: center; font-size: 11px; font-weight: 700; color: var(--cup-text-3); background: var(--cup-bg-subtle); border: 1px solid var(--cup-border); border-radius: 999px; padding: 3px 10px; white-space: nowrap; }
.cup-bracketscope .cup-tie-wrap { display: flex; align-items: center; }

.cup-bracketscope .cup-card { width: 100%; background: var(--cup-bg); border: 1px solid var(--cup-border); border-radius: 10px; box-shadow: 0 1px 2px rgba(20,40,80,0.04); padding: 8px 10px; transition: box-shadow 0.16s, border-color 0.16s, transform 0.16s; }
.cup-bracketscope .cup-card:hover { border-color: #C5D2E2; box-shadow: 0 8px 20px rgba(20,40,80,0.10); }
.cup-bracketscope .cup-card__rows { display: flex; flex-direction: column; gap: 5px; }
.cup-bracketscope .cup-team { display: flex; align-items: center; gap: 9px; min-width: 0; }
.cup-bracketscope .cup-team--dim { opacity: 0.46; }
.cup-bracketscope .cup-team__logo { border-radius: 5px; object-fit: contain; background: #fff; flex-shrink: 0; box-shadow: inset 0 0 0 1px rgba(0,0,0,0.06); }
.cup-bracketscope .cup-team__mono { display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0; color: #fff; font-weight: 800; letter-spacing: -0.02em; line-height: 1; font-family: inherit; background: linear-gradient(135deg, #2183FE 0%, #1E40AF 100%); box-shadow: inset 0 0 0 1px rgba(0,0,0,0.07); }
.cup-bracketscope .cup-team__name { flex: 1; min-width: 0; font-size: 13px; font-weight: 600; color: var(--cup-text); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; letter-spacing: -0.01em; display: inline-flex; align-items: center; gap: 6px; }
.cup-bracketscope .cup-team--win .cup-team__name { font-weight: 800; }
.cup-bracketscope .cup-team__sub { font-size: 9px; font-weight: 800; letter-spacing: 0.05em; text-transform: uppercase; color: var(--cup-text-3); background: var(--cup-bg-subtle); border: 1px solid var(--cup-border); padding: 1px 5px; border-radius: 999px; flex-shrink: 0; }
.cup-bracketscope .cup-team__score { flex-shrink: 0; }
.cup-bracketscope .cup-scorebox { display: inline-flex; min-width: 21px; height: 21px; padding: 0 5px; align-items: center; justify-content: center; font-size: 13px; font-weight: 800; font-variant-numeric: tabular-nums; color: var(--cup-text-2); border-radius: 6px; background: var(--cup-bg-subtle); }
.cup-bracketscope .cup-scorebox--win { background: var(--cup-primary); color: #fff; }
.cup-bracketscope .cup-vs { color: var(--cup-text-3); font-weight: 700; font-size: 13px; padding-right: 7px; }
.cup-bracketscope .cup-team__legs { display: inline-flex; gap: 4px; }
.cup-bracketscope .cup-leg { display: inline-flex; min-width: 19px; height: 21px; align-items: center; justify-content: center; font-size: 12px; font-weight: 800; font-variant-numeric: tabular-nums; color: var(--cup-text); background: var(--cup-bg-subtle); border-radius: 6px; }
.cup-bracketscope .cup-leg--ghost { color: var(--cup-text-3); }
.cup-bracketscope .cup-team__ph { width: 22px; height: 22px; border-radius: 6px; background: var(--cup-bg-subtle); border: 1px dashed var(--cup-border); flex-shrink: 0; }
.cup-bracketscope .cup-team__name--ph { color: var(--cup-text-3); }

.cup-bracketscope .cup-card__foot { display: flex; flex-wrap: wrap; gap: 5px; margin-top: 8px; align-items: center; }
.cup-bracketscope .cup-tag { font-size: 10px; font-weight: 700; letter-spacing: 0.01em; color: var(--cup-text-2); background: var(--cup-bg-subtle); border-radius: 5px; padding: 2px 6px; line-height: 1.35; white-space: nowrap; }
.cup-bracketscope .cup-tag--aet { color: #92580B; background: rgba(245,158,11,0.16); }
.cup-bracketscope .cup-tag--pens { color: var(--cup-primary); background: rgba(33,131,254,0.12); font-weight: 800; }
.cup-bracketscope .cup-tag--sched { color: var(--cup-primary); background: rgba(33,131,254,0.12); font-weight: 700; }
.cup-bracketscope .cup-tag--played { color: #0a7c52; background: rgba(16,185,129,0.14); font-weight: 700; }
.cup-bracketscope .cup-tag--agg { color: var(--cup-text); background: var(--cup-bg-subtle); font-weight: 800; }

.cup-bracketscope .cup-card--tbd { display: flex; align-items: center; border-style: dashed; background: var(--cup-bg-subtle); box-shadow: none; }
.cup-bracketscope .cup-card--tbd:hover { box-shadow: none; transform: none; }
.cup-bracketscope .cup-card__tbd { display: flex; align-items: center; gap: 8px; width: 100%; }
.cup-bracketscope .cup-card__tbd-ico { color: var(--cup-text-3); display: flex; flex-shrink: 0; font-size: 15px; }
.cup-bracketscope .cup-card__tbd-txt { font-size: 12px; font-weight: 700; color: var(--cup-text-2); }
.cup-bracketscope .cup-card__tbd-sub { margin-left: auto; font-size: 10px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.06em; color: var(--cup-text-3); }
.cup-bracketscope .cup-card--final { border-color: rgba(33,131,254,0.45); border-style: solid; background: linear-gradient(180deg, rgba(33,131,254,0.07), var(--cup-bg-subtle)); }
.cup-bracketscope .cup-card--final .cup-card__tbd-ico, .cup-bracketscope .cup-card--final .cup-card__tbd-txt, .cup-bracketscope .cup-card--final .cup-card__tbd-sub { color: var(--cup-primary); }

.cup-bracketscope .cup-scrollnav { position: absolute; top: 50%; transform: translateY(-50%); z-index: 6; width: 30px; height: 30px; border-radius: 999px; border: 1px solid var(--cup-border); background: #fff; color: var(--cup-text); display: inline-flex; align-items: center; justify-content: center; cursor: pointer; box-shadow: 0 6px 18px rgba(20,40,80,0.16); transition: opacity 0.18s, background 0.16s; }
.cup-bracketscope .cup-scrollnav--l { left: 8px; } .cup-bracketscope .cup-scrollnav--r { right: 8px; }
.cup-bracketscope .cup-scrollnav:hover { background: #F7FAFD; }
.cup-bracketscope .cup-scrollnav--off { opacity: 0; pointer-events: none; }
.cup-bracketscope .cup-fade { position: absolute; top: 0; bottom: 9px; width: 40px; pointer-events: none; z-index: 5; transition: opacity 0.18s; }
.cup-bracketscope .cup-fade--l { left: 0; background: linear-gradient(90deg, var(--cup-bg), rgba(255,255,255,0)); }
.cup-bracketscope .cup-fade--r { right: 0; background: linear-gradient(-90deg, var(--cup-bg), rgba(255,255,255,0)); }
.cup-bracketscope .cup-fade--off { opacity: 0; }

.cup-bracketscope .cup-empty { padding: 40px 28px; text-align: center; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 8px; flex: 1; }
.cup-bracketscope .cup-empty__ico { color: var(--cup-text-3); margin-bottom: 4px; font-size: 30px; }
.cup-bracketscope .cup-empty__title { font-size: 16px; font-weight: 800; color: var(--cup-text); }
.cup-bracketscope .cup-empty__sub { font-size: 13px; color: var(--cup-text-2); max-width: 380px; line-height: 1.5; }

.cup-legendbar { display: flex; gap: 12px; }
.cup-legendbar .cup-leg-item { display: inline-flex; align-items: center; gap: 6px; font-size: 11px; font-weight: 500; color: #6B7280; }
.cup-legendbar .cup-leg-item i { width: 8px; height: 8px; border-radius: 2px; display: inline-block; }
`;

export { CupLegend };
export default CupBracket;
